import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  actionOpenLaborTicketModal,
  actionOpenSetupActionModal,
} from 'lib/actions';
import { LABOR_TICKET_ACTIONS } from 'lib/constants';
import { getEmbeddedLaborTicketFlowEnabled } from 'lib/selectors/getEmbeddedLaborTicketFlowEnabled';
import { getSkipSetupParts } from 'lib/selectors/getSkipSetupParts';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';

/**
 * Returns a callback that starts the submit-work flow for the current labor
 * ticket context.
 *
 * In embedded labor flow, submit work opens the standard submit modal unless
 * the current ticket is a setup ticket and setup parts are skipped, in which
 * case it opens the setup action modal in submit mode.
 *
 * Outside embedded labor flow, submit work opens the labor ticket modal with
 * the stop-and-create action.
 */
const useStartSubmitWork = () => {
  const dispatch = useDispatch();

  const isEmbeddedLaborFlowEnabled = useSelector(
    getEmbeddedLaborTicketFlowEnabled
  );
  const skipSetupParts = useSelector(getSkipSetupParts);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const isSetupTicket = currentLaborTicket?.type?.toLowerCase() === 'setup';
  const useSetupActionModal = skipSetupParts && isSetupTicket;

  return useCallback(() => {
    if (isEmbeddedLaborFlowEnabled) {
      if (useSetupActionModal) {
        return dispatch(actionOpenSetupActionModal('submit'));
      }
      return dispatch(actionOpenLaborTicketModal('submit'));
    }
    return dispatch(
      actionOpenLaborTicketModal(LABOR_TICKET_ACTIONS.STOP_AND_CREATE)
    );
  }, [dispatch, isEmbeddedLaborFlowEnabled, useSetupActionModal]);
};

export default useStartSubmitWork;
